import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react';
import styles from './QuestionSectionCriteria.module.css';
import layout from '../../Modules/layout.module.css'
import Button from '../Utils/Button/Button';
import QuestionSectionResults from './QuestionSectionResults';
import { useAppDispatch, useAppSelector } from '../../Redux/Store/Store';
import { generateAssessment, lockQuestion, selectAreaDueDate, selectCriteriaById, selectQuestionById, selectRolePermissions, selectSectionById, setActiveCriteria, setActiveQuestionnaireSection, UpdateAreaDueDate, UpdateCriteriaDate,UpdateCriteriaGrade, UpdateCriteriaLowerField, updateQuestionnaireGrade, updateQuestionnaireQuestion, clearComments, setIsViewingResults as setIsViewingResultsAction, generateAnswersFromEvidence, acceptAiAnswer, acceptAllAiAnswers, revertAiAnswer, revertAllAiAnswers, selectIsGeneratingAnswersFromEvidence, selectGenerateAnswersProgressSteps, selectIsAcceptingAiAnswers, selectAiGeneratedAnswers, selectAiAnsweredQuestionIds, selectAiSkipReasonsByQuestionId, selectAiAnswerSnapshot, selectAiEvidenceSources, fetchCoachFeedback, selectCoachFeedbackByQuestionId, selectCoachStatusByQuestionId, selectCoachProgressByQuestionId, selectCoachedAnswerTextByQuestionId, clearAiAnswerPreview, hydrateCoachFeedbackFromQuestions, hydrateAiDraftsFromQuestions, updateCriteriaContextData } from '../../Redux/Store/Slices/questionnaire';
import Form from '../Utils/Form/Form';
import DatePicker from '../Utils/DatePicker/DatePicker';
import { deleteEvaluateFileLink, GetEvaluateLinkedEvidenceByCriteriaId, UpdateQuestionnaireGrade } from '../../API/Evaluate/Evaluate';
import Popup from '../Utils/Popup/Popup';
import type { SavedToLibraryItem } from '../Utils/AddEvidencePopup/AddEvidencePopup';
import CommentsPopup from '../Utils/CommentsPopup/CommentsPopup';
import ReusableCommentsPopup from '../Utils/CommentsPopup/ReusableCommentsPopup';
import type { DevelopRolePermission } from '../../Types/Roles/RolePermission';
import PermissionsPopup from '../PermissionsPopup/PermissionsPopup';
import CommentsIcon from '../Icons/CommentsIcon/CommentsIcon';
import ConfirmAction from '../Utils/ConfirmAction/ConfirmAction';
import Tooltip from '../Utils/Tooltip/Tooltip';
import { isSetDate, UNSET_DATE } from '../../Helpers/Dates';
import LinkedEvidencePopup, { LinkedEvidenceResponse } from '../Utils/LinkedEvidencePopup/LinkedEvidencePopup';
import DropDownList from '../Utils/DropDownList/DropDownList';
import { IQuestionnaireQuestion } from '../../Types/Products/IQuestionnaireQuestion';
import { distinctEvidenceSources, evidenceSourcesFromQuestions } from '../../Types/Products/ICriteriaItemData';
import { axiosInstance } from '../../Authentication/AxiosInterceptor';
import { toast } from 'react-toastify';
import { getClient, getAiPowered, getMiaCoachEnabled } from '../../Redux/Store/Slices/client';
import { LibraryFileRef } from '../../Types/Library/Library';
import axios from 'axios';
import { FileData } from '../Utils/FileItem/FileItem';
import type { PrefetchedConversationFile, PrefetchedLinkedEvidenceFile } from '../../Types/Evaluate/AreaCriteriaContextData';
import TagsBar from '../Utils/TagsBar/TagsBar';
import MultiSelect, { ISelectOption } from '../Utils/MultiSelect/MultiSelect';
import { ProductType } from '../../Types/ProductType';
import { ClientUserDisplayInfo } from '../../Types/ClientUserDisplayInfo';
import { IDropDownListItem } from '../../Types/DropDownList/IDropDownListItem';
import { UpdateLowerFieldType } from '../../Types/Develop/UpdateLowerFieldRequest';
import PillLabel from '../Utils/PillLabel/PillLabel';
import { getEffectiveNotesDefaultPermission } from './notesDefaultPermission';
import LinkedImprovementsPopup from '../Evaluate/LinkedImprovementsPopup/LinkedImprovementsPopup';
import EvidenceSourceSelector from '../Utils/EvidenceSourceSelector/EvidenceSourceSelector';
import { useUnsavedChangesWarning } from '../../Hooks/useUnsavedChangesWarning';
import { getFileIcon } from '../../Helpers/fileIcon';
import {
  deleteConversationFile,
  getEvaluateConversationFiles,
  uploadEvaluateConversationFile,
} from '../../API/ConversationFiles/ConversationFiles';
import HighlightedTextArea from '../Utils/Form/HighlightedTextArea/HighlightedTextArea';
import CoachFeedbackCard from '../Utils/CoachFeedbackCard/CoachFeedbackCard';
import { collectCoachHighlights, findCoachHighlightRanges } from '../../Utils/aiCoach';

const pageSize = 6;
const MCP_AUDIENCE = 'https://api.mesma.co.uk';
const COACH_DEBOUNCE_MS = 1500;

const QuestionSectionCriteria = () => {
  const { areaId, itemId, sectionId, criteriaId } = useParams<{ areaId: string; itemId?: string; sectionId?: string; criteriaId?: string; }>();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { activeCriteriaId, activeSectionId, questionnaire, comments, commentsLoading, linkedEvidenceByCriteriaId, conversationFilesByCriteriaId } = useAppSelector(state => state.questionnaire);

  const section = useAppSelector(selectSectionById(activeSectionId));
  const criteria = useAppSelector(selectCriteriaById(activeSectionId, activeCriteriaId));
  const rolePermissions = useAppSelector(selectRolePermissions());
  const areaDueDate = useAppSelector(selectAreaDueDate);
  const currentUserSub = useAppSelector((state) => state.auth.user?.sub);
  
  const client = useAppSelector(getClient);
  const isAiPowered = useAppSelector(getAiPowered);
  const isMiaCoachEnabled = useAppSelector(getMiaCoachEnabled);
  const isGeneratingAnswersFromEvidence = useAppSelector(selectIsGeneratingAnswersFromEvidence);
  const generateAnswersProgressSteps = useAppSelector(selectGenerateAnswersProgressSteps);
  const isAcceptingAiAnswers = useAppSelector(selectIsAcceptingAiAnswers);
  const aiGeneratedAnswers = useAppSelector(selectAiGeneratedAnswers);
  const aiAnsweredQuestionIds = useAppSelector(selectAiAnsweredQuestionIds);
  const aiSkipReasonsByQuestionId = useAppSelector(selectAiSkipReasonsByQuestionId);
  const aiAnswerSnapshot = useAppSelector(selectAiAnswerSnapshot);
  const coachFeedbackByQuestionId = useAppSelector(selectCoachFeedbackByQuestionId);
  const coachStatusByQuestionId = useAppSelector(selectCoachStatusByQuestionId);
  const coachProgressByQuestionId = useAppSelector(selectCoachProgressByQuestionId);
  const coachedAnswerTextByQuestionId = useAppSelector(selectCoachedAnswerTextByQuestionId);
  const aiEvidenceSources = useAppSelector(selectAiEvidenceSources);
  const { getAccessTokenSilently } = useAuth0();
  const isDevelop = questionnaire?.productType === ProductType.Develop;
  const isEvaluate = questionnaire?.productType === ProductType.Evaluate;
  const showPersonField = isDevelop && !!questionnaire?.personShown;
  const showPicklistField = isDevelop && !!questionnaire?.picklistShown;
  const showDropdownField = isDevelop && !!questionnaire?.dropdownShown;
  const showAnyDevelopOptionalField = showPersonField || showPicklistField || showDropdownField;
  const productPath = isEvaluate ? "evaluate" : "develop";
  const personFieldTitle = questionnaire?.personName?.trim() || "Person";
  const picklistFieldTitle = questionnaire?.picklistName?.trim() || "Picklist";
  const dropdownFieldTitle = questionnaire?.dropdownName?.trim() || "Dropdown";

  const evidenceCriteriaId = React.useMemo(
    () => activeCriteriaId || itemId || criteriaId || '',
    [activeCriteriaId, itemId, criteriaId]
  );

  // Check if current user has questionnaire permission (Read or Editor), respecting per-user overrides before default.
  const hasNotesPermission = React.useMemo(() => {
    if (rolePermissions?.canEditNotesPermissions) {
      return true;
    }

    if (
      questionnaire?.restrictClientAdminQuestionnaireAccess &&
      questionnaire.restrictClientAdminQuestionnaireLockedBySub?.trim()
    ) {
      return (
        questionnaire.restrictClientAdminQuestionnaireLockedBySub.toLowerCase() ===
        currentUserSub?.toLowerCase()
      );
    }

    const explicit = questionnaire?.notesPermissions?.find(
      (p) => p.userSub?.toLowerCase() === currentUserSub?.toLowerCase()
    );
    if (explicit) {
      if (explicit.role === "None") return false;
      if (explicit.role === "Read" || explicit.role === "Editor") return true;
    }

    const defaultPermission = getEffectiveNotesDefaultPermission(questionnaire);
    if (defaultPermission === "Read" || defaultPermission === "Editor") {
      return true;
    }

    if (!currentUserSub || !questionnaire?.notesPermissions) {
      return false;
    }

    return questionnaire.notesPermissions.some(
      (perm) =>
        perm.userSub?.toLowerCase() === currentUserSub.toLowerCase() &&
        (perm.role === "Read" || perm.role === "Editor")
    );
  }, [
    currentUserSub,
    questionnaire?.restrictClientAdminQuestionnaireAccess,
    questionnaire?.restrictClientAdminQuestionnaireLockedBySub,
    questionnaire?.notesPermissions,
    questionnaire?.notesDefaultPermission,
    rolePermissions?.canEditNotesPermissions,
  ]);

  // Users can only edit questionnaire answers if they have notes edit access.
  const hasNotesEditPermission = React.useMemo(() => {
    if (rolePermissions?.canEditNotesPermissions) {
      return true;
    }

    if (
      questionnaire?.restrictClientAdminQuestionnaireAccess &&
      questionnaire.restrictClientAdminQuestionnaireLockedBySub?.trim()
    ) {
      return (
        questionnaire.restrictClientAdminQuestionnaireLockedBySub.toLowerCase() ===
        currentUserSub?.toLowerCase()
      );
    }

    const explicit = questionnaire?.notesPermissions?.find(
      (p) => p.userSub?.toLowerCase() === currentUserSub?.toLowerCase()
    );
    if (explicit) {
      if (explicit.role === "None" || explicit.role === "Read") return false;
      if (explicit.role === "Editor") return true;
    }

    const defaultPermission = getEffectiveNotesDefaultPermission(questionnaire);
    if (defaultPermission === "Editor") {
      return true;
    }

    if (!currentUserSub || !questionnaire?.notesPermissions) {
      return false;
    }

    return questionnaire.notesPermissions.some(
      (perm) =>
        perm.userSub?.toLowerCase() === currentUserSub.toLowerCase() && perm.role === "Editor"
    );
  }, [
    currentUserSub,
    questionnaire?.restrictClientAdminQuestionnaireAccess,
    questionnaire?.restrictClientAdminQuestionnaireLockedBySub,
    questionnaire?.notesPermissions,
    questionnaire?.notesDefaultPermission,
    rolePermissions?.canEditNotesPermissions,
  ]);

  const canEditQuestionnaireAnswers = !!rolePermissions?.editQuestionnaireContent && hasNotesEditPermission;
  const questionnaireEditTooltipText = !rolePermissions?.editQuestionnaireContent
    ? `Your role as a ${rolePermissions?.roleTitle} means that you cannot edit the questionnaire answers`
    : "Your questionnaire permission is set to read-only. You need the 'Edit' questionnaire permission to edit the questionnaire answers.";

  const lockerSubEffective = questionnaire?.restrictClientAdminQuestionnaireLockedBySub?.trim();
  const isDeniedDueToLockerLock =
    !!questionnaire?.restrictClientAdminQuestionnaireAccess &&
    !!lockerSubEffective &&
    lockerSubEffective.toLowerCase() !== currentUserSub?.toLowerCase();

  const [lockerDisplayName, setLockerDisplayName] = useState<string | null>(null);

  useEffect(() => {
    if (!areaId || areaId === 'undefined' || !lockerSubEffective) {
      setLockerDisplayName(null);
      return;
    }
    let cancelled = false;
    const fetchLockerName = async () => {
      try {
        const response = await axiosInstance.get<ClientUserDisplayInfo[]>(`GetAreaUserDisplayInfos/${areaId}`);
        const match = response.data?.find((u) => u.sub?.toLowerCase() === lockerSubEffective.toLowerCase());
        if (!cancelled) {
          setLockerDisplayName(match ? (match.fullName || match.email || match.sub) : null);
        }
      } catch {
        if (!cancelled) setLockerDisplayName(null);
      }
    };
    fetchLockerName();
    return () => {
      cancelled = true;
    };
  }, [areaId, lockerSubEffective]);

  const viewDiagnosticQuestionnaireDeniedTooltip = React.useMemo(() => {
    if (isDeniedDueToLockerLock) {
      const name = lockerDisplayName?.trim() || 'the user who locked it';
      return `This diagnostic questionnaire is locked by ${name}. Only they can access it or turn off the lock in Privacy Settings.`;
    }
    return 'You do not have permission to view the diagnostic questionnaire. Only the diagnostic owner, leads, and users invited to the questionnaire can access it.';
  }, [isDeniedDueToLockerLock, lockerDisplayName]);

  const generateAssessmentDeniedTooltip = React.useMemo(() => {
    if (isDeniedDueToLockerLock) {
      const name = lockerDisplayName?.trim() || 'the user who locked it';
      return `You cannot generate an assessment while the questionnaire is locked by ${name}. Only they can unlock it from Privacy Settings.`;
    }
    return 'You need permission to view the diagnostic questionnaire to generate an assessment. Only the diagnostic owner, leads, and users invited to the questionnaire can access it.';
  }, [isDeniedDueToLockerLock, lockerDisplayName]);

  const [activePage, setActivePage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [isViewingResults, setIsViewingResults] = useState<boolean>(false);
  const [generatingAssessment, setGeneratingAssessment] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);

  const [commentsVisible, setCommentsVisible] = useState<boolean>(false);
  const [permissionsPopupVisible, setPermissionsPopupVisible] = useState<boolean>(false);
  const [evidenceVisible, setEvidenceVisible] = useState<boolean>(false);
  const [linkedEvidencePopupLoading, setLinkedEvidencePopupLoading] = useState(false);
  const [linkedImprovementsVisible, setLinkedImprovementsVisible] = useState<boolean>(false);
  const [confirmOverwriteVisible, setConfirmOverwriteVisible] = useState<boolean>(false);
  const [guidanceVisible, setGuidanceVisible] = useState<boolean>(false);
  const [criteriaGuidance, setCriteriaGuidance] = useState<string>('');
  const [reflectionsCommentsVisible, setReflectionsCommentsVisible] = useState<boolean>(false);
  const [lineManagerCommentsVisible, setLineManagerCommentsVisible] = useState<boolean>(false);
  const [mentorCommentsVisible, setMentorCommentsVisible] = useState<boolean>(false);
  const [otherCommentsVisible, setOtherCommentsVisible] = useState<boolean>(false);

  // Sync local isViewingResults state to Redux for the navbar video feature
  useEffect(() => {
    dispatch(setIsViewingResultsAction(isViewingResults));
  }, [isViewingResults, dispatch]);

  useEffect(() => {
    return () => { dispatch(setIsViewingResultsAction(false)); };
  }, [dispatch]);

  // Local state for questionnaire answers to prevent cursor jumping
  const [localAnswers, setLocalAnswers] = useState<{[key: string]: string}>({});
  const [comparingDraftIds, setComparingDraftIds] = useState<Record<string, boolean>>({});
  const [hasEditedAnswers, setHasEditedAnswers] = useState(false);

  // Only resync from Redux when switching criteria / question set — not when
  // coach insights (or other metadata) patch the same questions mid-typing.
  const criteriaAnswersSyncKey = `${activeCriteriaId}:${criteria?.questions?.map((question) => question.id).join(',') ?? ''}`;

  useEffect(() => {
    if (!criteria?.questions) {
      return;
    }
    const answers = criteria.questions.reduce((acc, question) => {
      acc[question.id] = question.draftAnswer?.trim()
        ? question.draftAnswer
        : (question.answer ?? '');
      return acc;
    }, {} as {[key: string]: string});
    setLocalAnswers(answers);
    setHasEditedAnswers(false);
    // criteria.questions is read for initial values; the sync key captures identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaAnswersSyncKey]);

  // Warn on tab/browser close while local edits haven't been persisted yet
  const hasUnsavedQuestionnaireAnswers = useMemo(() => {
    if (!hasEditedAnswers || !criteria?.questions?.length) return false;
    return criteria.questions.some((question) => {
      const local = localAnswers[question.id];
      if (local === undefined) return false;
      return (local ?? '') !== (question.answer ?? '');
    });
  }, [criteria?.questions, localAnswers, hasEditedAnswers]);

  useUnsavedChangesWarning(hasUnsavedQuestionnaireAnswers);

  useEffect(() => {
    if (!aiGeneratedAnswers || !isAiPowered) {
      return;
    }
    setLocalAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [questionId, draft] of Object.entries(aiGeneratedAnswers)) {
        if (prev[questionId] === draft) {
          continue;
        }
        // Don't replace text the user is still typing (saved on blur).
        if (hasEditedAnswers && prev[questionId] !== undefined) {
          continue;
        }
        next[questionId] = draft;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [aiGeneratedAnswers, isAiPowered, hasEditedAnswers]);

  useEffect(() => {
    if (!isAiPowered) {
      dispatch(clearAiAnswerPreview());
    }
  }, [isAiPowered, dispatch]);

    // Summarise criteria comments are loaded with GetQuestionnaireByAreaId and applied on criteria change.
  useEffect(() => {
    if (isDevelop) {
      dispatch(clearComments());
    }
  }, [criteriaId, dispatch, isDevelop]);

  useEffect(() => {
    if (sectionId && sectionId !== activeSectionId) {
      dispatch(setActiveQuestionnaireSection({ sectionId }));
    }
  }, [dispatch, sectionId, activeSectionId]);

  useEffect(() => {

    const init = async () => {

      if (itemId && itemId !== activeCriteriaId) {
        await dispatch(setActiveCriteria({ itemId: itemId }));
      }
    }
    init();
    
  }, [dispatch, itemId, activeCriteriaId]);

  // Update activeCriteriaId when criteriaId changes
  useEffect(() => {
    if (criteriaId && criteriaId !== activeCriteriaId) {
      dispatch(setActiveCriteria({ itemId: criteriaId }));
    }
  }, [criteriaId, activeCriteriaId, dispatch]);

  useEffect(() => {
    if (questionnaire?.legacyVersion || !isAiPowered || criteria?.questions.length === 0) {
      setIsViewingResults(true);
    } else {
      const pages = Math.ceil((criteria?.questions?.length ?? 0) / pageSize);
      setPageCount(pages);
      setActivePage(1);

      const assessmentGenerated = criteria?.generatedResults ?? false;
      // If results were never generated, only default to questionnaire mode when notes access is allowed.
      setIsViewingResults(assessmentGenerated ? true : !hasNotesPermission);
    }
  }, [activeCriteriaId, questionnaire?.legacyVersion, isAiPowered, criteria?.questions?.length, criteria?.generatedResults, hasNotesPermission]);

  const computedStatus = React.useMemo<"Complete" | "Incomplete" | "In Progress">(() => {
  // prefer section.criteria if that’s your item list; fall back to section.items
  const items = (section as any)?.criteria ?? (section as any)?.items ?? [];
  const total = Array.isArray(items) ? items.length : 0;
  if (total === 0) return "Incomplete"; // or "In Progress"/hide, up to you

  const checkedCount = items.filter((x: any) => x?.checked).length;
  if (checkedCount === 0) return "Incomplete";
  if (checkedCount === total) return "Complete";
  return "In Progress";
}, [section]);

  const nextPageClickHandler = async () => {
    if(isViewingResults){
      setIsViewingResults(false)
    }
    else if (activePage === pageCount) {
      setIsViewingResults(true)
      setActivePage(1)

      if(!criteria?.generatedResults){
        await generateAssessmentHandler();
      }
    }
    else {
      setActivePage(activePage + 1)
    }
  }

  const generateAssessmentHandler = async () => {
    if(!isViewingResults){
      var response = await axiosInstance.get(`CheckAssessmentGenerated/${criteriaId}`);
      if(response.data === true){
        toast.warn("Assessment has been generated by another user. Showing the current results");
        setIsViewingResults(true);
        return;
      }
    }
    setGeneratingAssessment(true);
    try {
      // Persist visible answers (including AI drafts) so generation matches the UI.
      if (criteria?.questions?.length) {
        await Promise.all(
          criteria.questions.map((question) => {
            const answer = localAnswers[question.id] ?? question.answer ?? "";
            if ((answer || "") === (question.answer || "")) {
              return Promise.resolve();
            }
            return dispatch(
              updateQuestionnaireQuestion({ ...question, answer })
            ).unwrap();
          })
        );
      }

      const token = await getAccessTokenSilently({
        authorizationParams: { audience: MCP_AUDIENCE },
      });
      await dispatch(
        generateAssessment({
          token,
          clientId: client?.id || "",
          answerOverrides: localAnswers,
        })
      ).unwrap();
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "Could not generate the assessment.";
      toast.error(message);
    } finally {
      setGeneratingAssessment(false);
    }
  }

  const handleGenerateAgainClick = () => {
    // If there are already generated results, show confirmation popup
    if (criteria?.generatedResults) {
      setConfirmOverwriteVisible(true);
    } else {
      // If no existing results, generate directly
      generateAssessmentHandler();
    }
  }

  const handleConfirmOverwrite = async () => {
    setConfirmOverwriteVisible(false);
    await generateAssessmentHandler();
  }

  const checkFieldChanged = async (question: IQuestionnaireQuestion) => {

    try {

      const updatedContent = await axiosInstance.put(
        `CheckQuestionnaireFieldChanged/${question.id}`,
        question.answer
      );

      dispatch(lockQuestion({questionId: question.id, locked: false}));

      const newAnswer = updatedContent.data;

      if(newAnswer !== question.answer){
        setLocalAnswers((prev) => ({ ...prev, [question.id]: newAnswer }));
        await dispatch(updateQuestionnaireQuestion({...question, answer: newAnswer}));
        toast.warn("Changes were made to this field by another user");
      }

    } catch (error) {

      if (axios.isAxiosError(error) && error.response?.status === 409) {
        dispatch(lockQuestion({ questionId: question.id, locked: true }));
        //toast.error("Another user is currently editing this answer. Please try again later.");
        return;
      }
    }
  }

  const handleViewGuidance = () => {
    // The fullCriteria is already loaded with the criteria object
    const guidance = criteria?.fullCriteria || '';
    setCriteriaGuidance(guidance);
    setGuidanceVisible(true);
  }

  const [files, setFiles] = useState<FileData[]>([]);
  const [linkedEvidenceFiles, setLinkedEvidenceFiles] = useState<FileData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<string[]>([]);
  const [evaluateConversationId, setEvaluateConversationId] = useState<string | undefined>(undefined);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [removingEvidenceId, setRemovingEvidenceId] = useState<string | null>(null);
  const [insufficientEvidenceMessage, setInsufficientEvidenceMessage] = useState<string | null>(null);
  const insufficientEvidenceFileIdsRef = React.useRef<string[]>([]);
  // Tracks whether we've already restored selection for this criteria from persisted sources.
  const evidenceRestoredForCriteriaRef = React.useRef<string | null>(null);

  useEffect(() => {
    setSelectedEvidenceIds([]);
    setEvaluateConversationId(undefined);
    setFiles([]);
    setLinkedEvidenceFiles([]);
    setInsufficientEvidenceMessage(null);
    insufficientEvidenceFileIdsRef.current = [];
    setRemovingEvidenceId(null);
    setError(null);
    setComparingDraftIds({});
    evidenceRestoredForCriteriaRef.current = null;
  }, [activeCriteriaId]);

  // Clear the insufficient-evidence banner once a new file is added.
  useEffect(() => {
    if (!insufficientEvidenceMessage) {
      return;
    }
    const knownIds = new Set(insufficientEvidenceFileIdsRef.current);
    const hasNewFile = files.some((file) => file.id && !knownIds.has(file.id));
    if (hasNewFile) {
      setInsufficientEvidenceMessage(null);
      insufficientEvidenceFileIdsRef.current = [];
    }
  }, [files, insufficientEvidenceMessage]);

  // Restore Mia Coach insights and AI drafts persisted on question documents.
  useEffect(() => {
    if (!criteria?.questions?.length) {
      return;
    }
    dispatch(hydrateAiDraftsFromQuestions(criteria.questions));
    dispatch(hydrateCoachFeedbackFromQuestions(criteria.questions));
    // Don't re-hydrate when coach insights patch the same questions mid-typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteriaAnswersSyncKey, dispatch]);

  // Union of evidence ids previously saved on answers + current AI draft sources.
  const persistedEvidenceIds = useMemo(() => {
    const ids = new Set<string>();
    criteria?.questions?.forEach((question) => {
      question.evidenceSources?.forEach((source) => {
        if (source.id) {
          ids.add(source.id);
        }
      });
    });
    aiEvidenceSources.forEach((source) => {
      if (source.id) {
        ids.add(source.id);
      }
    });
    return ids;
  }, [criteria?.questions, aiEvidenceSources]);

  // When linked files load, restore selection for this criteria.
  // Prefer evidence previously used for AI answers; otherwise select all linked files
  // (matching "evidence you add is selected automatically").
  useEffect(() => {
    if (loading || files.length === 0) {
      return;
    }
    if (evidenceRestoredForCriteriaRef.current === activeCriteriaId) {
      return;
    }

    const fromPersisted = files
      .filter((file) => persistedEvidenceIds.has(file.id))
      .map((file) => file.id);

    const restoredIds =
      fromPersisted.length > 0
        ? fromPersisted
        : files.filter((file) => file.isAvailable !== false).map((file) => file.id);

    evidenceRestoredForCriteriaRef.current = activeCriteriaId;
    setSelectedEvidenceIds((prev) => Array.from(new Set([...prev, ...restoredIds])));
  }, [files, loading, persistedEvidenceIds, activeCriteriaId]);

  // Snapshot stored on the criteria when the summary was generated.
  // Fall back to distinct answer-generation sources for older summaries.
  const summaryEvidenceForResults = useMemo(() => {
    const stored = distinctEvidenceSources(criteria?.summaryEvidenceSources);
    const sources =
      stored.length > 0
        ? stored
        : criteria?.generatedResults
          ? evidenceSourcesFromQuestions(criteria.questions)
          : [];

    return sources
      .filter((source) => source.fileName)
      .map((source) => ({
        id: source.id || source.fileName,
        fileName: source.fileName,
      }));
  }, [criteria?.summaryEvidenceSources, criteria?.generatedResults, criteria?.questions]);

  const mapPrefetchedLinkedToFileData = useCallback((items: PrefetchedLinkedEvidenceFile[]): FileData[] =>
    items.map((file) => ({
      id: file.id,
      linkId: file.linkId,
      fileName: file.fileName,
      portfolioId: file.portfolioId,
      folderId: file.folderId,
      isAvailable: file.isAvailable,
      source: 'library' as const,
    })), []);

  const mapPrefetchedConversationToFileData = useCallback((items: PrefetchedConversationFile[]): FileData[] =>
    items.map((file) => ({
      id: file.fileId,
      fileName: file.fileName,
      portfolioId: '',
      folderId: '',
      source: 'uploaded' as const,
      conversationId: file.conversationId,
    })), []);

  const buildEvidenceFromStore = useCallback((
    criteriaId: string,
    linkedMap: Record<string, PrefetchedLinkedEvidenceFile[]>,
    conversationMap: Record<string, PrefetchedConversationFile[]>,
  ) => {
    const libraryFiles = mapPrefetchedLinkedToFileData(linkedMap[criteriaId] ?? []);
    const uniqueLibraryFiles = libraryFiles.filter(
      (file, index, all) =>
        all.findIndex(
          (candidate) =>
            candidate.portfolioId === file.portfolioId &&
            candidate.folderId === file.folderId &&
            candidate.fileName === file.fileName
        ) === index
    );

    const uploadedRaw = conversationMap[criteriaId] ?? [];
    const uploadedFileData = mapPrefetchedConversationToFileData(uploadedRaw);
    const uploadedIds = new Set(uploadedFileData.map((file) => file.id));
    const evidenceFiles = [
      ...uniqueLibraryFiles.filter((file) => !uploadedIds.has(file.id)),
      ...uploadedFileData,
    ];

    return {
      libraryFiles: uniqueLibraryFiles,
      evidenceFiles,
      uploadedRaw,
    };
  }, [mapPrefetchedLinkedToFileData, mapPrefetchedConversationToFileData]);

  const applyEvidenceFromStore = useCallback((criteriaId: string) => {
    const { libraryFiles, evidenceFiles, uploadedRaw } = buildEvidenceFromStore(
      criteriaId,
      linkedEvidenceByCriteriaId,
      conversationFilesByCriteriaId,
    );
    setLinkedEvidenceFiles(libraryFiles);
    setFiles(evidenceFiles);

    const ownUpload = uploadedRaw.find(
      (file) => file.uploadedBy?.toLowerCase() === currentUserSub?.toLowerCase()
    );
    if (ownUpload?.conversationId) {
      setEvaluateConversationId(ownUpload.conversationId);
    } else if (uploadedRaw.length > 0) {
      setEvaluateConversationId(uploadedRaw[uploadedRaw.length - 1].conversationId);
    }
  }, [
    buildEvidenceFromStore,
    linkedEvidenceByCriteriaId,
    conversationFilesByCriteriaId,
    currentUserSub,
  ]);

  useEffect(() => {
    if (isDevelop || !evidenceCriteriaId) {
      setFiles([]);
      setLinkedEvidenceFiles([]);
      setLoading(false);
      setError(null);
      return;
    }

    applyEvidenceFromStore(evidenceCriteriaId);
    setLoading(false);
    setError(null);
  }, [
    isDevelop,
    evidenceCriteriaId,
    applyEvidenceFromStore,
    linkedEvidenceByCriteriaId,
    conversationFilesByCriteriaId,
  ]);

  const mapLibraryResponse = useCallback((libraryResponse: { files?: Record<string, unknown>[] }) =>
    (libraryResponse.files || []).map((file: Record<string, unknown>) => ({
      id: String(file.id ?? file.Id ?? ''),
      linkId: String(file.linkId ?? file.LinkId ?? ''),
      fileName: String(file.fileName ?? file.FileName ?? ''),
      portfolioId: String(file.portfolioId ?? file.PortfolioId ?? ''),
      folderId: String(file.folderId ?? file.FolderId ?? ''),
      isAvailable: file.isAvailable !== false && file.IsAvailable !== false,
    })) as PrefetchedLinkedEvidenceFile[], []);

  const refreshLinkedLibraryEvidence = useCallback(async () => {
    if (!evidenceCriteriaId || !areaId) {
      return;
    }

    try {
      setError(null);
      const libraryResponse = await GetEvaluateLinkedEvidenceByCriteriaId(evidenceCriteriaId, areaId);
      const linkedEvidence = mapLibraryResponse(libraryResponse);

      dispatch(updateCriteriaContextData({
        criteriaId: evidenceCriteriaId,
        linkedEvidence,
      }));

      return buildEvidenceFromStore(
        evidenceCriteriaId,
        { ...linkedEvidenceByCriteriaId, [evidenceCriteriaId]: linkedEvidence },
        conversationFilesByCriteriaId,
      );
    } catch (err) {
      console.error('Error refreshing linked evidence:', err);
      setError('Failed to load linked evidence files. Please try again.');
    }
  }, [
    evidenceCriteriaId,
    areaId,
    dispatch,
    mapLibraryResponse,
    buildEvidenceFromStore,
    linkedEvidenceByCriteriaId,
    conversationFilesByCriteriaId,
  ]);

  const refreshCriteriaEvidence = useCallback(async () => {
    if (!evidenceCriteriaId || !areaId || !client?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [libraryResponse, uploadedFiles] = await Promise.all([
        GetEvaluateLinkedEvidenceByCriteriaId(evidenceCriteriaId, areaId),
        getEvaluateConversationFiles(areaId, evidenceCriteriaId, client.id),
      ]);

      const linkedEvidence = mapLibraryResponse(libraryResponse);

      const conversationFiles = uploadedFiles.map((file) => ({
        fileId: file.fileId,
        fileName: file.fileName,
        extension: file.extension,
        fileSize: file.fileSize,
        conversationId: file.conversationId,
        uploadedAt: file.uploadedAt,
        uploadedBy: file.uploadedBy,
      })) as PrefetchedConversationFile[];

      dispatch(updateCriteriaContextData({
        criteriaId: evidenceCriteriaId,
        linkedEvidence,
        conversationFiles,
      }));

      return buildEvidenceFromStore(
        evidenceCriteriaId,
        { ...linkedEvidenceByCriteriaId, [evidenceCriteriaId]: linkedEvidence },
        { ...conversationFilesByCriteriaId, [evidenceCriteriaId]: conversationFiles },
      );
    } catch (err) {
      console.error('Error refreshing linked evidence:', err);
      setError('Failed to load linked evidence files. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [evidenceCriteriaId, areaId, client?.id, dispatch, mapLibraryResponse, buildEvidenceFromStore, linkedEvidenceByCriteriaId, conversationFilesByCriteriaId]);

  const refreshLinkedLibraryEvidenceRef = React.useRef(refreshLinkedLibraryEvidence);
  refreshLinkedLibraryEvidenceRef.current = refreshLinkedLibraryEvidence;

  useEffect(() => {
    if (!evidenceVisible || isDevelop || !evidenceCriteriaId || !areaId) {
      return;
    }

    let cancelled = false;
    setLinkedEvidencePopupLoading(true);

    refreshLinkedLibraryEvidenceRef.current()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setLinkedEvidencePopupLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [evidenceVisible, isDevelop, evidenceCriteriaId, areaId]);

  const handleEvidenceAdded = useCallback(
    async (linkedRefs: LibraryFileRef[]) => {
      if (!evidenceCriteriaId || !areaId || !client?.id) return;

      try {
        const { evidenceFiles } = (await refreshCriteriaEvidence()) ?? { evidenceFiles: [] as FileData[] };

        const newSelectedIds = evidenceFiles
          .filter((file) =>
            linkedRefs.some(
              (ref) =>
                file.portfolioId === ref.portfolioId &&
                file.folderId === ref.folderId &&
                file.fileName === ref.fileName
            )
          )
          .map((file) => file.id);

        setSelectedEvidenceIds((prev) => Array.from(new Set([...prev, ...newSelectedIds])));
      } catch (err) {
        console.error('Error fetching linked evidence:', err);
        setError('Failed to load linked evidence files. Please try again.');
      }
    },
    [evidenceCriteriaId, areaId, client?.id, refreshCriteriaEvidence]
  );

  const handleSavedToLibrary = useCallback(
    async (saved: SavedToLibraryItem[]) => {
      if (saved.length === 0) return;
      try {
        const { evidenceFiles } = (await refreshCriteriaEvidence()) ?? { evidenceFiles: [] as FileData[] };
        const savedUploadIds = new Set(saved.map((item) => item.uploadedFileId));
        const libraryIds = evidenceFiles
          .filter((file) =>
            saved.some(
              (item) =>
                file.portfolioId === item.libraryRef.portfolioId &&
                file.folderId === item.libraryRef.folderId &&
                file.fileName === item.libraryRef.fileName
            )
          )
          .map((file) => file.id);
        setSelectedEvidenceIds((prev) =>
          Array.from(new Set([...prev.filter((id) => !savedUploadIds.has(id)), ...libraryIds]))
        );
      } catch (err) {
        console.error('Error refreshing evidence saved to the library:', err);
      }
    },
    [refreshCriteriaEvidence]
  );

  const handleUploadedFilesSelected = useCallback(
    async (fileIds: string[]) => {
      try {
        await refreshCriteriaEvidence();
        setSelectedEvidenceIds((prev) => Array.from(new Set([...prev, ...fileIds])));
      } catch (err) {
        console.error('Error refreshing uploaded evidence:', err);
        setSelectedEvidenceIds((prev) => Array.from(new Set([...prev, ...fileIds])));
      }
    },
    [refreshCriteriaEvidence]
  );

  const handleRemoveEvidence = useCallback(
    async (file: FileData) => {
      if (
        !areaId ||
        !client?.id ||
        !rolePermissions?.editGeneratedContent ||
        isGeneratingAnswersFromEvidence
      ) {
        return;
      }

      setRemovingEvidenceId(file.id);
      setError(null);

      try {
        if (file.source === 'uploaded') {
          await deleteConversationFile(file.id, client.id);
        } else {
          const linkedForCriteria = linkedEvidenceByCriteriaId[evidenceCriteriaId] ?? [];
          const matchingLinkIds = Array.from(
            new Set(
              linkedForCriteria
                .filter((linked) => {
                  if (file.linkId && linked.linkId === file.linkId) {
                    return true;
                  }
                  return (
                    !!file.portfolioId &&
                    !!file.folderId &&
                    !!file.fileName &&
                    linked.portfolioId === file.portfolioId &&
                    linked.folderId === file.folderId &&
                    linked.fileName === file.fileName
                  );
                })
                .map((linked) => linked.linkId)
                .filter((linkId): linkId is string => !!linkId)
            )
          );

          const linkIdsToDelete =
            matchingLinkIds.length > 0
              ? matchingLinkIds
              : file.linkId
                ? [file.linkId]
                : [];

          if (linkIdsToDelete.length === 0) {
            toast.error('Unable to unlink this library file.');
            return;
          }

          await Promise.all(
            linkIdsToDelete.map((linkId) => deleteEvaluateFileLink(areaId, linkId))
          );
        }

        setSelectedEvidenceIds((prev) => prev.filter((id) => id !== file.id));
        await refreshCriteriaEvidence();
        toast.success(
          file.source === 'uploaded'
            ? 'Uploaded file deleted.'
            : 'Library file unlinked from this criteria.'
        );
      } catch (err) {
        console.error('Error removing evidence:', err);
        toast.error('Failed to remove evidence. Please try again.');
      } finally {
        setRemovingEvidenceId(null);
      }
    },
    [
      areaId,
      client?.id,
      rolePermissions?.editGeneratedContent,
      isGeneratingAnswersFromEvidence,
      linkedEvidenceByCriteriaId,
      evidenceCriteriaId,
      refreshCriteriaEvidence,
    ]
  );

  const handleUploadEvidence = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList?.length || !areaId || !evidenceCriteriaId || !client?.id) {
        return;
      }

      const file = fileList[0];
      setUploadingEvidence(true);
      setError(null);

      try {
        const response = await uploadEvaluateConversationFile(
          file,
          client.id,
          areaId,
          evidenceCriteriaId,
          evaluateConversationId
        );

        setEvaluateConversationId(response.conversationId);

        const uploadedFile: FileData = {
          id: response.fileId,
          fileName: response.fileName,
          portfolioId: '',
          folderId: '',
          source: 'uploaded',
          conversationId: response.conversationId,
        };

        setFiles((prev) => {
          if (prev.some((f) => f.id === uploadedFile.id)) {
            return prev;
          }
          return [...prev, uploadedFile];
        });
        setSelectedEvidenceIds((prev) => Array.from(new Set([...prev, response.fileId])));
        toast.success('File uploaded and ready for AI generation.');
      } catch (err) {
        console.error('Error uploading evidence file:', err);
        toast.error('Failed to upload file. Please try again.');
      } finally {
        setUploadingEvidence(false);
      }
    },
    [areaId, evidenceCriteriaId, client?.id, evaluateConversationId]
  );

  // ── MIA Coach: realtime per-question guidance ──
  const coachEnabled = isEvaluate && isMiaCoachEnabled && canEditQuestionnaireAnswers && !!client?.id;
  const coachTimersRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Coach soft-checks the current Auto-fill selection (chips), not stale
  // evidenceSources stamped when a draft was accepted.
  const getCoachEvidenceFiles = useCallback(
    (): FileData[] => files.filter((file) => selectedEvidenceIds.includes(file.id)),
    [files, selectedEvidenceIds]
  );

  const requestCoachFeedback = useCallback(
    async (question: IQuestionnaireQuestion, answerText: string, conversationId?: string) => {
      if (!coachEnabled || !client?.id || !areaId || !criteria) {
        return;
      }

      // Skip if this exact text has already been coached.
      if (coachedAnswerTextByQuestionId[question.id] === answerText) {
        return;
      }

      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: MCP_AUDIENCE },
        });

        await dispatch(
          fetchCoachFeedback({
            token,
            clientId: client.id,
            areaId,
            criteriaId: evidenceCriteriaId || criteria.id,
            criteriaTitle: criteria.title,
            fullCriteria: criteria.fullCriteria ?? '',
            questionId: question.id,
            questionText: question.question,
            answerText,
            evidenceFiles: getCoachEvidenceFiles(),
            conversationId,
          })
        ).unwrap();
      } catch (err) {
        console.error('Error fetching coach feedback:', err);
      }
    },
    [coachEnabled, client?.id, areaId, criteria, evidenceCriteriaId, coachedAnswerTextByQuestionId, getAccessTokenSilently, dispatch, getCoachEvidenceFiles]
  );

  const scheduleCoachFeedback = useCallback(
    (question: IQuestionnaireQuestion, answerText: string) => {
      if (!coachEnabled || isGeneratingAnswersFromEvidence) {
        return;
      }

      const timers = coachTimersRef.current;
      const existing = timers.get(question.id);
      if (existing) {
        clearTimeout(existing);
      }

      timers.set(
        question.id,
        setTimeout(() => {
          timers.delete(question.id);
          requestCoachFeedback(question, answerText);
        }, COACH_DEBOUNCE_MS)
      );
    },
    [coachEnabled, isGeneratingAnswersFromEvidence, requestCoachFeedback]
  );

  // Drop pending coach requests when leaving the criteria or unmounting.
  useEffect(() => {
    const timers = coachTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, [activeCriteriaId]);

  const handleGenerateAnswers = async () => {
    if (
      !criteria?.questions?.length ||
      !client?.id ||
      !areaId ||
      selectedEvidenceIds.length === 0 ||
      isGeneratingAnswersFromEvidence
    ) {
      return;
    }

    const selectedFiles = files.filter((file) => selectedEvidenceIds.includes(file.id));
    if (selectedFiles.length === 0) {
      toast.warn('Please select at least one evidence file.');
      return;
    }

    const hasUploadedEvidence = selectedFiles.some((file) => file.source === 'uploaded');
    const generationConversationId =
      selectedFiles.find((file) => file.source === 'uploaded')?.conversationId ||
      evaluateConversationId;

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: MCP_AUDIENCE },
      });

      const result = await dispatch(
        generateAnswersFromEvidence({
          token,
          clientId: client.id,
          areaId,
          criteriaId: evidenceCriteriaId || criteria.id,
          questions: criteria.questions,
          evidenceFiles: selectedFiles,
          criteriaTitle: criteria.title,
          sectionTitle: section?.title ?? '',
          conversationId: hasUploadedEvidence ? generationConversationId : undefined,
          // Resume the same agent conversation for this criteria + user.
          contextId: evidenceCriteriaId || criteria.id,
        })
      ).unwrap();

      if (result.allSkippedWithReasons) {
        const firstSkipReason =
          Object.values(result.skipReasonsByQuestionId).find((reason) => reason.trim().length > 0) ||
          'Evidence was not sufficient to draft answers to these questions. Add a more relevant file and try again.';
        setInsufficientEvidenceMessage(firstSkipReason);
        insufficientEvidenceFileIdsRef.current = files.map((file) => file.id);
        toast.warn(firstSkipReason);
        return;
      }

      setInsufficientEvidenceMessage(null);
      insufficientEvidenceFileIdsRef.current = [];
      setLocalAnswers((prev) => ({ ...prev, ...result.aiGeneratedAnswers }));

      // Coach every generated draft in parallel so feedback appears faster.
      const sharedCoachConversationId = crypto.randomUUID().replace(/-/g, '');
      await Promise.allSettled(
        result.aiAnsweredQuestionIds.map((questionId) => {
          const question = criteria.questions.find((q) => q.id === questionId);
          const draftText = result.aiGeneratedAnswers[questionId];
          if (question && draftText) {
            return requestCoachFeedback(question, draftText, sharedCoachConversationId);
          }
          return Promise.resolve();
        })
      );

      if ('parseWarning' in result && result.parseWarning) {
        toast.warn(
          'Could not parse AI response as JSON. The full response was placed in question 1.'
        );
      } else if (result.missingQuestionNumbers.length > 0) {
        toast.warn(
          `Drafted ${result.answeredCount} of ${result.totalQuestions} questions from the evidence. Questions without enough evidence were left blank.`
        );
      } else {
        toast.success(
          `Generated draft answers for ${result.answeredCount} questions. Review and accept when ready.`
        );
      }
    } catch (err) {
      console.error('Error generating answers from evidence:', err);
      const message =
        typeof err === 'string'
          ? err
          : 'Failed to generate answers from evidence. Please try again.';
      if (typeof err === 'string' && err.includes('not sufficient')) {
        setInsufficientEvidenceMessage(err);
        insufficientEvidenceFileIdsRef.current = files.map((file) => file.id);
        toast.warn(message);
      } else {
        toast.error(message);
      }
    }
  };

  const handleAcceptAll = async () => {
    if (!criteria?.questions?.length || isAcceptingAiAnswers || aiAnsweredQuestionIds.length === 0) {
      return;
    }

    try {
      const result = await dispatch(
        acceptAllAiAnswers({
          questions: criteria.questions,
          previewAnswers: localAnswers,
        })
      ).unwrap();

      if (result.failedIds.length > 0) {
        toast.warn(
          `${result.succeededIds.length} answer(s) saved. ${result.failedIds.length} could not be saved and remain as drafts (they may be locked by another user).`
        );
      } else {
        toast.success('Answers accepted and saved successfully.');
      }
    } catch (err) {
      console.error('Error accepting AI-generated answers:', err);
      const message =
        typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: string }).message)
          : typeof err === 'string'
            ? err
            : 'Failed to save answers. Please try again.';
      toast.error(message);
    }
  };

  const handleAcceptOne = async (question: IQuestionnaireQuestion) => {
    if (
      isAcceptingAiAnswers ||
      (!aiAnsweredQuestionIds.includes(question.id) && !question.draftAnswer?.trim())
    ) {
      return;
    }

    try {
      await dispatch(
        acceptAiAnswer({
          question,
          answer: localAnswers[question.id] ?? question.draftAnswer ?? question.answer,
        })
      ).unwrap();

      setComparingDraftIds((prev) => {
        const next = { ...prev };
        delete next[question.id];
        return next;
      });

      toast.success('Answer accepted and saved.');
    } catch (err) {
      console.error('Error accepting AI-generated answer:', err);
      const message =
        typeof err === 'string' ? err : 'Failed to save the answer. It may be locked by another user.';
      toast.error(message);
    }
  };

  const handleRevertAll = () => {
    if (!aiAnswerSnapshot || aiAnsweredQuestionIds.length === 0 || isAcceptingAiAnswers) {
      return;
    }

    const restoredAnswers = aiAnsweredQuestionIds.reduce(
      (acc, questionId) => {
        acc[questionId] = aiAnswerSnapshot[questionId] ?? '';
        return acc;
      },
      {} as Record<string, string>
    );

    const questionsToClear = criteria?.questions.filter((q) =>
      aiAnsweredQuestionIds.includes(q.id)
    ) ?? [];

    dispatch(revertAllAiAnswers());
    setLocalAnswers((prev) => ({ ...prev, ...restoredAnswers }));
    setComparingDraftIds({});

    questionsToClear.forEach((question) => {
      dispatch(
        updateQuestionnaireQuestion({
          ...question,
          draftAnswer: '',
          draftEvidenceSources: [],
          answer: aiAnswerSnapshot[question.id] ?? question.answer,
        })
      );
    });

    toast.info('AI draft answers reverted.');
  };

  const handleRevertOne = (questionId: string) => {
    if (!aiAnswerSnapshot || !aiAnsweredQuestionIds.includes(questionId) || isAcceptingAiAnswers) {
      return;
    }

    const previousAnswer = aiAnswerSnapshot[questionId] ?? '';
    const question = criteria?.questions.find((q) => q.id === questionId);
    dispatch(revertAiAnswer(questionId));
    setLocalAnswers((prev) => ({ ...prev, [questionId]: previousAnswer }));
    setComparingDraftIds((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });

    if (question) {
      dispatch(
        updateQuestionnaireQuestion({
          ...question,
          draftAnswer: '',
          draftEvidenceSources: [],
          answer: previousAnswer,
        })
      );
    }

    toast.info('Answer reverted to previous version.');
  };

  const toggleCompareDraft = (questionId: string) => {
    setComparingDraftIds((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

    const selectedTagsChanged = (selectedOptions: ISelectOption[]) => {
      if (criteria && areaId) {
        dispatch(UpdateCriteriaLowerField({
          criteriaId: criteria.id,
          updateType: UpdateLowerFieldType.UpdateTags,
          listData: selectedOptions.map(option => option.value),
        }));
      }
    };

    const personChanged = (selectedOptions: ISelectOption[]) => {
      if (criteria && areaId) {
        dispatch(UpdateCriteriaLowerField({
          criteriaId: criteria.id,
          updateType: UpdateLowerFieldType.UpdatePerson,
          listData: selectedOptions.map(option => option.value),
        }));
      }
    };

    const handlePicklistChange = (selectedOptions: ISelectOption[]) => {
      if (criteria && areaId) {
        dispatch(UpdateCriteriaLowerField({
          criteriaId: criteria.id,
          updateType: UpdateLowerFieldType.UpdatePicklist,
          listData: selectedOptions.map(option => option.value),
        }));
      }
    };

    const dropdownChange = (option: IDropDownListItem) => {
      if (criteria && areaId) {
        dispatch(UpdateCriteriaLowerField({
          criteriaId: criteria.id,
          updateType: UpdateLowerFieldType.UpdateDropdown,
          data: option.value,
        }));
      }
    };

  if(criteria && section){

    return (
      <>
      <div className={styles.page}>

        {/* ── Hero ── */}
        <div className={styles.hero}>
          <div className={styles.heroTop}>
            <div>
              <PillLabel label={section.title ?? ""} className={styles.heroPill} />
              <h1 className={styles.heroTitle}>{criteria.title}</h1>
            </div>
            <div className={`${styles.heroActions} ${styles.heroTopActions}`}>
              {!isViewingResults && (
                <span className={styles.pageCount}>
                  Page {activePage} of {pageCount}
                </span>
              )}
            </div>
          </div>
          <div className={styles.heroMeta}>
            <Tooltip text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit the date`} align={'top'} sideAlign={"right"} disabled={rolePermissions?.editDate}>
              <div className={styles.heroActions}>
                <span className={styles.metaLabel}>Completion Date</span>
                <DatePicker
                  readonly={!rolePermissions?.editDate}
                  value={criteria.date}
                  autoDefaultToday={false}
                  onChange={(date) =>
                    dispatch(
                      UpdateCriteriaDate({
                        criteriaId: activeCriteriaId,
                        sectionId: activeSectionId,
                        activityId: areaId || "",
                        date: date ?? UNSET_DATE,
                      })
                    )
                  }
                />
              </div>
            </Tooltip>
            <Tooltip text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit the date`} align={'top'} sideAlign={"right"} disabled={rolePermissions?.editDate}>
              <div className={styles.heroActions}>
                <span className={styles.metaLabel}>Due Date</span>
                <DatePicker
                  readonly={!rolePermissions?.editDate}
                  value={areaDueDate}
                  autoDefaultToday={false}
                  onChange={(date) => {
                    // Due date is required on the area, so ignore clears
                    if (isSetDate(date) && areaId) {
                      dispatch(UpdateAreaDueDate({ areaId, dueDate: date }));
                    }
                  }}
                />
              </div>
            </Tooltip>
            <div className={`${styles.heroActions} ${styles.heroMetaActions}`}>
              <Button
                size="Small"
                colour="#FFF"
                textColour="#000"
                border="rgba(0,0,0,0.15)"
                clickHandler={handleViewGuidance}
              >
                View Guidance
              </Button>
              {!isDevelop && (
                <Button
                  size="Small"
                  colour="#FFF"
                  textColour="#000"
                  border="rgba(0,0,0,0.15)"
                  clickHandler={() => setEvidenceVisible(true)}
                >
                  View Linked Evidence
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* ── Popups (always rendered) ── */}
        {!isDevelop && (
          <Popup isVisible={evidenceVisible} onClose={() => setEvidenceVisible(false)} allowSnapToSide={true}>
            <LinkedEvidencePopup
              files={linkedEvidenceFiles}
              fileCount={linkedEvidenceFiles.length}
              title={criteria?.title}
              loading={linkedEvidencePopupLoading || loading}
              error={error || undefined}
              canLinkEvidence={rolePermissions?.editGeneratedContent || false}
              roleName={rolePermissions?.roleTitle || ''}
              areaId={areaId}
              onFileLinkRemoved={refreshLinkedLibraryEvidence}
              linkPortfolio={
                areaId && client?.id && currentUserSub && evidenceCriteriaId
                  ? {
                      areaId,
                      questionId: evidenceCriteriaId,
                      linkType: 'Evaluate',
                      clientId: client.id,
                      userSub: currentUserSub,
                      subjectNoun: 'criteria',
                      onLinked: () => {
                        void refreshLinkedLibraryEvidence();
                      },
                    }
                  : undefined
              }
            />
          </Popup>
        )}
        {!isDevelop && (
          <Popup isVisible={linkedImprovementsVisible} onClose={() => setLinkedImprovementsVisible(false)}>
            <LinkedImprovementsPopup areaId={areaId} criteriaId={criteriaId || activeCriteriaId || itemId} />
          </Popup>
        )}

        <Popup
          isVisible={guidanceVisible}
          onClose={() => setGuidanceVisible(false)}
          allowSnapToSide={true}
        >
          <div className={styles.guidancePopup}>
            <h3>Criteria Guidance</h3>
            <div className={styles.guidanceContent}>
              {criteriaGuidance ? (
                <p>{criteriaGuidance}</p>
              ) : (
                <p className={styles.noGuidance}>There is no guidance for this section</p>
              )}
            </div>
          </div>
        </Popup>

        {/* ── Questions Mode ── */}
        {!isViewingResults && isAiPowered && criteria?.questions.length > 0 ?
          <>
            {isEvaluate && (
              <div className={styles.card}>
                <EvidenceSourceSelector
                  files={files}
                  loading={loading}
                  selectedIds={selectedEvidenceIds}
                  onSelectionChange={setSelectedEvidenceIds}
                  onRemoveEvidence={handleRemoveEvidence}
                  removingFileId={
                    removingEvidenceId ??
                    (isGeneratingAnswersFromEvidence ? 'generating' : null)
                  }
                  canLinkEvidence={rolePermissions?.editGeneratedContent || false}
                  roleName={rolePermissions?.roleTitle || ''}
                  title="Auto-fill answers from evidence"
                  hint="Select evidence files to help Mia pre-fill the diagnostic question answers below. The X unlinks a library file or deletes an uploaded file."
                  alertMessage={insufficientEvidenceMessage}
                  addEvidenceContext={
                    areaId && client?.id && currentUserSub && evidenceCriteriaId
                      ? {
                          areaId,
                          questionId: evidenceCriteriaId,
                          linkType: 'Evaluate',
                          clientId: client.id,
                          userSub: currentUserSub,
                          contextLabel: criteria?.title,
                          onEvidenceAdded: handleEvidenceAdded,
                          onUploadedFilesSelected: handleUploadedFilesSelected,
                          onUploadedFilesChanged: refreshCriteriaEvidence,
                          onSavedToLibrary: handleSavedToLibrary,
                          onUploadConversationFile: handleUploadEvidence,
                          uploadingConversationFile: uploadingEvidence,
                        }
                      : undefined
                  }
                />
                <div className={styles.aiActionsRow}>
                  <Button
                    disabled={isGeneratingAnswersFromEvidence || selectedEvidenceIds.length === 0}
                    colour="#24394F"
                    textColour="#FFF"
                    clickHandler={handleGenerateAnswers}
                  >
                    {isGeneratingAnswersFromEvidence ? 'Generating...' : 'Generate answers'}
                    <img src="/img/sparkle.svg" alt="" />
                  </Button>
                  {aiAnsweredQuestionIds.length > 0 && (
                    <>
                      <Button
                        colour="#52BFA2"
                        textColour="#FFF"
                        clickHandler={handleAcceptAll}
                        disabled={isAcceptingAiAnswers}
                      >
                        {isAcceptingAiAnswers ? 'Saving...' : 'Accept all'}
                      </Button>
                      <Button
                        colour="#FFF"
                        textColour="#000"
                        border="rgba(0,0,0,0.15)"
                        clickHandler={handleRevertAll}
                        disabled={isAcceptingAiAnswers}
                      >
                        Revert all
                      </Button>
                    </>
                  )}
                </div>
                {isGeneratingAnswersFromEvidence && (
                  <div className={styles.generateProgressPanel} role="status" aria-live="polite">
                    <div className={styles.generateProgressHeader}>
                      <div className={styles.generateProgressOrb}>
                        <span className={styles.generateProgressOrbPulse} aria-hidden="true" />
                        <span className={styles.generateProgressOrbRing} aria-hidden="true" />
                        <img
                          className={styles.generateProgressOrbLogo}
                          src="/img/logo.png"
                          alt=""
                        />
                      </div>
                      <div className={styles.generateProgressBody}>
                        <span className={styles.generateProgressTitle}>
                          Mia is generating your answers
                        </span>
                        <div className={styles.generateProgressTrack}>
                          <div className={styles.generateProgressBar} />
                        </div>
                      </div>
                    </div>
                    <ol className={styles.generateProgressSteps}>
                      {generateAnswersProgressSteps.map((step, index) => {
                        const isActive = index === generateAnswersProgressSteps.length - 1;
                        return (
                          <li
                            key={`${index}-${step}`}
                            className={`${styles.generateProgressStep} ${isActive ? styles.generateProgressStepActive : styles.generateProgressStepDone}`}
                          >
                            <span className={styles.generateStepMarker}>
                              {isActive ? (
                                <span className={styles.generateStepSpinner} />
                              ) : (
                                <svg viewBox="0 0 12 12" className={styles.generateStepCheck} aria-hidden="true">
                                  <path d="M2.5 6.5 L5 9 L9.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </span>
                            <span className={styles.generateStepText}>{step}</span>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                )}
              </div>
            )}
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <div>
                  <p className={styles.cardTitle}>Diagnostic Questions</p>
                  <p className={styles.cardHint}>Answer the questions below to generate your assessment.</p>
                </div>
              </div>
              <Form>
                {criteria?.questions.map((item) => {
                  const isAiDraft = aiAnsweredQuestionIds.includes(item.id) || !!item.draftAnswer?.trim();
                  const skipReason = !isAiDraft ? aiSkipReasonsByQuestionId[item.id] : undefined;
                  const draftValue =
                    localAnswers[item.id] !== undefined
                      ? localAnswers[item.id]
                      : aiGeneratedAnswers?.[item.id] ?? item.draftAnswer ?? item.answer;
                  const currentAnswer =
                    aiAnswerSnapshot?.[item.id] !== undefined
                      ? aiAnswerSnapshot[item.id]
                      : item.answer ?? '';
                  const answerValue = isAiDraft ? draftValue : (localAnswers[item.id] !== undefined ? localAnswers[item.id] : item.answer);
                  const isComparing = !!comparingDraftIds[item.id] && isAiDraft;
                  const coachFeedback = coachFeedbackByQuestionId[item.id] ?? item.coachInsights;
                  const coachStatus = coachStatusByQuestionId[item.id];
                  const coachedText =
                    coachedAnswerTextByQuestionId[item.id] ??
                    item.coachInsights?.answerText ??
                    (item.coachInsights ? draftValue : undefined);
                  // Feedback is stale once the text moves past what was coached;
                  // highlights are hidden then so marks never drift mid-typing.
                  const isCoachStale = !!coachFeedback && coachedText !== answerValue;
                  const highlightRanges = coachEnabled && coachFeedback && !isCoachStale
                    ? findCoachHighlightRanges(answerValue ?? '', collectCoachHighlights(coachFeedback))
                    : [];
                  return <Tooltip key={item.id} text={questionnaireEditTooltipText} align={'top'} disabled={canEditQuestionnaireAnswers}>
                    <div className={`${styles.questionAnswerRow} ${isAiDraft ? styles.questionAnswerRowDraft : ''} ${skipReason ? styles.questionAnswerRowSkipped : ''}`}>
                      {skipReason && (
                        <div className={styles.skipReasonBanner} role="status">
                          <span className={styles.skipReasonBadge}>Not auto-filled</span>
                          <span className={styles.skipReasonText}>{skipReason}</span>
                        </div>
                      )}
                      {isAiDraft && (
                        <div className={styles.draftHeaderRow}>
                          <span className={styles.draftBadge}>
                            <img src="/img/sparkle.svg" alt="" />
                            AI draft — not yet accepted
                          </span>
                          <button
                            type="button"
                            className={styles.compareToggle}
                            onClick={() => toggleCompareDraft(item.id)}
                            aria-pressed={isComparing}
                          >
                            {isComparing ? 'Hide comparison' : 'Compare with current'}
                          </button>
                        </div>
                      )}
                      {isComparing ? (
                        <div className={styles.compareGrid}>
                          <div className={styles.compareColumn}>
                            <span className={styles.compareLabel}>Current answer</span>
                            <HighlightedTextArea
                              suspended={false}
                              readonly
                              label={`${criteria.questions.indexOf(item) + 1}.   ${item.question}`}
                              value={currentAnswer}
                              highlightRanges={[]}
                            />
                          </div>
                          <div className={styles.compareColumn}>
                            <span className={styles.compareLabel}>AI draft</span>
                            <HighlightedTextArea
                              suspended={item.locked}
                              onBlur={() => {
                                dispatch(
                                  updateQuestionnaireQuestion({
                                    ...item,
                                    answer: currentAnswer,
                                    draftAnswer: localAnswers[item.id] ?? item.draftAnswer ?? '',
                                    evidenceSources: item.evidenceSources,
                                    coachInsights: item.coachInsights,
                                  })
                                );
                              }}
                              onFocus={() => {
                                if (canEditQuestionnaireAnswers) {
                                  checkFieldChanged(item);
                                }
                              }}
                              readonly={!canEditQuestionnaireAnswers}
                              label={`${criteria.questions.indexOf(item) + 1}.   ${item.question}`}
                              onChange={(value) => {
                                if (value !== answerValue) {
                                  setHasEditedAnswers(true);
                                  scheduleCoachFeedback(item, value);
                                }
                                setLocalAnswers(prev => ({...prev, [item.id]: value}));
                              }}
                              value={answerValue}
                              highlightRanges={highlightRanges}
                            />
                          </div>
                        </div>
                      ) : (
                        <HighlightedTextArea
                          suspended={item.locked}
                          onBlur={() => {
                            if (isAiDraft) {
                              dispatch(
                                updateQuestionnaireQuestion({
                                  ...item,
                                  answer: currentAnswer,
                                  draftAnswer: localAnswers[item.id] ?? item.draftAnswer ?? '',
                                  evidenceSources: item.evidenceSources,
                                  coachInsights:
                                    coachFeedbackByQuestionId[item.id]
                                      ? {
                                          ...coachFeedbackByQuestionId[item.id],
                                          answerText:
                                            coachedAnswerTextByQuestionId[item.id] ?? answerValue,
                                        }
                                      : item.coachInsights,
                                })
                              );
                              return;
                            }
                            const latestFeedback = coachFeedbackByQuestionId[item.id];
                            const coachInsights = latestFeedback
                              ? {
                                  ...latestFeedback,
                                  answerText: coachedAnswerTextByQuestionId[item.id] ?? answerValue,
                                }
                              : item.coachInsights;
                            dispatch(
                              updateQuestionnaireQuestion({
                                ...item,
                                answer: localAnswers[item.id] ?? item.answer ?? '',
                                coachInsights,
                                evidenceSources: item.evidenceSources,
                              })
                            );
                          }}
                          onFocus={() => {
                            if (canEditQuestionnaireAnswers) {
                              checkFieldChanged(item);
                            }
                          }}
                          readonly={!canEditQuestionnaireAnswers}
                          label={`${criteria.questions.indexOf(item) + 1}.   ${item.question}`}
                          onChange={(value) => {
                            // FloatingLabelTextArea emits onChange on mount/prop
                            // sync too; only real edits should (re)schedule coaching.
                            if (value !== answerValue) {
                              setHasEditedAnswers(true);
                              scheduleCoachFeedback(item, value);
                            }
                            setLocalAnswers(prev => ({...prev, [item.id]: value}));
                          }}
                          value={answerValue}
                          highlightRanges={highlightRanges}
                        />
                      )}
                      {coachEnabled && (
                        <CoachFeedbackCard
                          feedback={coachFeedback}
                          isLoading={coachStatus === 'loading'}
                          progressText={coachProgressByQuestionId[item.id]}
                          isStale={isCoachStale}
                          status={coachStatus}
                          onRetry={() => requestCoachFeedback(item, answerValue ?? '')}
                        />
                      )}
                      {isAiDraft && (
                        <div className={styles.draftActions}>
                          <button
                            type="button"
                            className={styles.acceptButton}
                            onClick={() => handleAcceptOne(item)}
                            disabled={isAcceptingAiAnswers}
                          >
                            Accept ✓
                          </button>
                          <button
                            type="button"
                            className={styles.revertButton}
                            onClick={() => handleRevertOne(item.id)}
                            disabled={isAcceptingAiAnswers}
                          >
                            Revert ↩
                          </button>
                        </div>
                      )}
                      {isAiDraft && item.draftEvidenceSources && item.draftEvidenceSources.length > 0 && (
                        <div className={styles.evidenceSourcesRow}>
                          <span className={styles.evidenceSourcesLabel}>Evidence used:</span>
                          {item.draftEvidenceSources.map((source) => (
                            <span key={source.id} className={styles.evidenceSourceChip}>
                              <img src={getFileIcon(source.fileName)} alt="" />
                              {source.fileName}
                            </span>
                          ))}
                        </div>
                      )}
                      {!isAiDraft && item.evidenceSources && item.evidenceSources.length > 0 && (
                        <div className={styles.evidenceSourcesRow}>
                          <span className={styles.evidenceSourcesLabel}>Evidence used:</span>
                          {item.evidenceSources.map((source) => (
                            <span key={source.id} className={styles.evidenceSourceChip}>
                              <img src={getFileIcon(source.fileName)} alt="" />
                              {source.fileName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Tooltip>
                })}
              </Form>
            </div>

            <div className={styles.actionBar}>
              <div className={styles.actionBarTop}>
                <Tooltip text={`Your role as a ${rolePermissions?.roleTitle || 'user'} means that you cannot update questionnaire access permissions.`} sideAlign="left" align="top" disabled={rolePermissions?.canEditNotesPermissions}>
                  <Button
                    colour={"#24394F"}
                    textColour="#FFF"
                    clickHandler={() => setPermissionsPopupVisible(true)}
                    disabled={!rolePermissions?.canEditNotesPermissions}
                  >
                    <img src="/img/gear.svg" alt="" className={styles.permissionsButtonIcon} />
                    Privacy Settings
                  </Button>
                </Tooltip>
                <Popup isVisible={permissionsPopupVisible} onClose={() => setPermissionsPopupVisible(false)}>
                  <PermissionsPopup areaId={areaId} />
                </Popup>
                <Tooltip text="AI powered feature. Please upgrade to access." align="top" disabled={isAiPowered}>
                  <Button
                    disabled={!isAiPowered}
                    colour={"#24394F"}
                    clickHandler={nextPageClickHandler}
                  >
                    {activePage === pageCount ? "Check My Responses" : "Next"}
                    {activePage === pageCount && <img src='/img/sparkle.svg' />}
                  </Button>
                </Tooltip>
              </div>
            </div>
          </>
        :
          <>
            {/* ── Results Card ── */}
            <QuestionSectionResults
              completed={completed}
              loading={generatingAssessment}
              showAiInsights={isEvaluate && isAiPowered}
              selectedFiles={summaryEvidenceForResults}
            />

            {/* ── Additional Fields Card (Develop) ── */}
            {showAnyDevelopOptionalField && criteria && (
              <div className={styles.card}>
                <div className={styles.fieldsGrid}>
                  <div className={styles.fieldsTopRow}>
                    {showPersonField && (
                      <Tooltip text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit the ${personFieldTitle} field`} align="top" sideAlign="right" disabled={rolePermissions?.editGeneratedContent}>
                        <div className={styles.fieldGroup}>
                          <label className={styles.fieldLabel}>{personFieldTitle}</label>
                          <MultiSelect
                            availableOptions={(criteria.personData ?? []).map(p => ({ value: p.value, label: p.text, required: false }))}
                            selectedOptions={(Array.isArray(criteria.person) ? criteria.person : criteria.person ? [criteria.person] : []).map(sub => {
                              const item = criteria.personData?.find(d => d.value === sub);
                              return { value: sub, label: item?.text ?? sub, required: false };
                            })}
                            onChange={personChanged}
                            placeholder="Select person(s)..."
                            noOptionsMessage="No options available"
                            readonly={!rolePermissions?.editGeneratedContent}
                          />
                        </div>
                      </Tooltip>
                    )}
                    {showDropdownField && (
                      <Tooltip text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit the ${dropdownFieldTitle} field`} align="top" sideAlign="right" disabled={rolePermissions?.editGeneratedContent}>
                        <div className={styles.fieldGroup}>
                          <label className={styles.fieldLabel}>{dropdownFieldTitle}</label>
                          <DropDownList
                            placeholder="Select option..."
                            value={criteria?.dropdown || ""}
                            data={criteria.dropdownData}
                            noOptionsMessage="No options available"
                            onChange={dropdownChange}
                            readonly={!rolePermissions?.editGeneratedContent}
                          />
                        </div>
                      </Tooltip>
                    )}
                  </div>
                  {showPicklistField && (
                    <Tooltip text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit the ${picklistFieldTitle} field`} align="top" sideAlign="right" disabled={rolePermissions?.editGeneratedContent}>
                      <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>{picklistFieldTitle}</label>
                        <MultiSelect
                          availableOptions={criteria.picklistData}
                          selectedOptions={criteria.picklist}
                          onChange={handlePicklistChange}
                          noOptionsMessage="No options available"
                          readonly={!rolePermissions?.editGeneratedContent}
                        />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}

            {/* ── Grade, Comments & Tags Card ── */}
            <div className={styles.card}>
              <div className={styles.controlsRow}>
                <div className={styles.controlsTopRow}>
                  {!isDevelop && (
                    <Button clickHandler={() => setCommentsVisible(true)} colour={"#FFF"} className={styles.commentsButton}>
                      <CommentsIcon className={styles.commentsIcon} size={18} />
                      <span className={styles.commentsText}>
                        {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
                      </span>
                    </Button>
                  )}

                  <Tooltip text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit the grade`} align={'top'} disabled={rolePermissions?.editQuestionGrade}>
                    <div className={styles.gradeGroup}>
                      <span className={styles.gradeLabel}>Grade:</span>
                      <DropDownList
                        onChange={(x) => dispatch(UpdateCriteriaGrade({
                          gradeId: x.value,
                          criteriaId: criteria.id,
                          questionnaireId: questionnaire?.id ?? "",
                          gradeProfileId: questionnaire?.gradeProfileId ?? ""
                        }))}
                        placeholder={'Select a grade'}
                        data={questionnaire?.gradeOptions ?? []}
                        value={criteria.grade}
                        readonly={!rolePermissions?.editQuestionGrade}
                      />
                    </div>
                  </Tooltip>
                </div>

                <Tooltip text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit Tags`} align="top" sideAlign="left" disabled={rolePermissions?.editGeneratedContent}>
                  <div className={styles.gradeGroup}>
                    <span className={styles.gradeLabel}>Tags:</span>
                    <div className={styles.tagsBar}>
                      <MultiSelect
                        availableOptions={criteria.tagsData}
                        selectedOptions={criteria.tags.map(tagId => {
                          const option = criteria.tagsData.find(o => o.value === tagId);
                          return option
                            ? { value: option.value, label: option.label, required: false }
                            : { value: tagId, label: tagId, required: false };
                        })}
                        placeholder="Select tags"
                        onChange={selectedTagsChanged}
                        readonly={!rolePermissions?.editGeneratedContent}
                      />
                    </div>
                  </div>
                </Tooltip>
              </div>
            </div>

            {/* ── Comment Popups ── */}
            {!isDevelop && (
              <Popup isVisible={commentsVisible} onClose={() => setCommentsVisible(false)} allowSnapToSide={true}>
                <CommentsPopup contextId={criteriaId} authorizationContextId={areaId}/>
              </Popup>
            )}

            <Popup isVisible={reflectionsCommentsVisible} onClose={() => setReflectionsCommentsVisible(false)} allowSnapToSide={true}>
              {areaId && (
                <div className={styles.developCommentsPopup}>
                  <h3 className={styles.developCommentsTitle}>Reflections Comments</h3>
                  <ReusableCommentsPopup
                    contextId={areaId}
                    apiMode="develop"
                    commentType="Reflections"
                    progressMode={false}
                    canCreate={true /* TODO: restore (rolePermissions as unknown as DevelopRolePermission)?.addEditDeleteReflectionsComments ?? false */}
                    canEdit={true /* TODO: restore role check */}
                    canDelete={true /* TODO: restore role check */}
                  />
                </div>
              )}
            </Popup>

            <Popup isVisible={lineManagerCommentsVisible} onClose={() => setLineManagerCommentsVisible(false)} allowSnapToSide={true}>
              {areaId && (
                <div className={styles.developCommentsPopup}>
                  <h3 className={styles.developCommentsTitle}>Line Manager Comments</h3>
                  <ReusableCommentsPopup
                    contextId={areaId}
                    apiMode="develop"
                    commentType="LineManager"
                    progressMode={false}
                    canCreate={true /* TODO: restore (rolePermissions as unknown as DevelopRolePermission)?.addEditDeleteLineManagerComments ?? false */}
                    canEdit={true /* TODO: restore role check */}
                    canDelete={true /* TODO: restore role check */}
                  />
                </div>
              )}
            </Popup>

            <Popup isVisible={mentorCommentsVisible} onClose={() => setMentorCommentsVisible(false)} allowSnapToSide={true}>
              {areaId && (
                <div className={styles.developCommentsPopup}>
                  <h3 className={styles.developCommentsTitle}>Mentor Comments</h3>
                  <ReusableCommentsPopup
                    contextId={areaId}
                    apiMode="develop"
                    commentType="Mentor"
                    progressMode={false}
                    canCreate={true /* TODO: restore (rolePermissions as unknown as DevelopRolePermission)?.addEditDeleteMentorComments ?? false */}
                    canEdit={true /* TODO: restore role check */}
                    canDelete={true /* TODO: restore role check */}
                  />
                </div>
              )}
            </Popup>

            <Popup isVisible={otherCommentsVisible} onClose={() => setOtherCommentsVisible(false)} allowSnapToSide={true}>
              {areaId && (
                <div className={styles.developCommentsPopup}>
                  <h3 className={styles.developCommentsTitle}>Other Comments</h3>
                  <ReusableCommentsPopup
                    contextId={areaId}
                    apiMode="develop"
                    commentType="Other"
                    progressMode={false}
                    canCreate={true /* TODO: restore (rolePermissions as unknown as DevelopRolePermission)?.addEditDeleteLearnerComments ?? false */}
                    canEdit={true /* TODO: restore role check */}
                    canDelete={true /* TODO: restore role check */}
                  />
                </div>
              )}
            </Popup>

            <ConfirmAction
              isVisible={isAiPowered && confirmOverwriteVisible}
              onClose={() => setConfirmOverwriteVisible(false)}
              clickHandler={handleConfirmOverwrite}
              title=""
              message="Generating again will overwrite your current response"
              confirmText="Generate Again"
              cancelText="Cancel"
              icon="/img/sparkle.svg"
              severity="warning"
              loading={generatingAssessment}
            />

            {/* ── Sticky Action Bar (only when there are visible actions) ── */}
            {(isDevelop || isEvaluate || !questionnaire?.legacyVersion) && (
              <div className={styles.actionBar}>
                <div className={styles.actionBarTopSticky}>
                  {(isDevelop || isEvaluate) && (
                    <div className={styles.actionBarLeft}>
                      {isDevelop && (
                        <div className={styles.segmentedComments}>
                          <button type="button" onClick={() => setReflectionsCommentsVisible(true)}>
                            <CommentsIcon className={styles.commentsIcon} size={14} />
                            Reflections
                          </button>
                          <button type="button" onClick={() => setLineManagerCommentsVisible(true)}>
                            <CommentsIcon className={styles.commentsIcon} size={14} />
                            Line Manager
                          </button>
                          <button type="button" onClick={() => setMentorCommentsVisible(true)}>
                            <CommentsIcon className={styles.commentsIcon} size={14} />
                            Mentor
                          </button>
                          <button type="button" onClick={() => setOtherCommentsVisible(true)}>
                            <CommentsIcon className={styles.commentsIcon} size={14} />
                            Other
                          </button>
                        </div>
                      )}

                      <button
                        type="button"
                        className={styles.createObjectiveBtn}
                        onClick={() => {
                          const targetSectionId = sectionId || activeSectionId;
                          const targetCriteriaId = criteriaId || itemId || criteria?.id || activeCriteriaId;

                          if (!areaId || !targetSectionId || !targetCriteriaId) {
                            navigate(`/${productPath}/${areaId}/create-improvement`);
                            return;
                          }

                          navigate(
                            `/${productPath}/${areaId}/create-improvement?sectionId=${encodeURIComponent(targetSectionId)}&criteriaId=${encodeURIComponent(targetCriteriaId)}&itemId=${encodeURIComponent(targetCriteriaId)}`,
                          );
                        }}
                      >
                        + Create Improvement
                      </button>
                      {isEvaluate && (
                        <button
                          type="button"
                          className={styles.createObjectiveBtn}
                          onClick={() => setLinkedImprovementsVisible(true)}
                        >
                          View Linked Improvements
                        </button>
                      )}
                    </div>
                  )}

                  {isAiPowered && !questionnaire?.legacyVersion && (
                    <div className={styles.generateBackGroup}>
                      <Tooltip text="There are no diagnostic questions found in this criteria." align="top" sideAlign="left" disabled={criteria?.questions.length > 0 || !hasNotesPermission}>
                        <Tooltip text={viewDiagnosticQuestionnaireDeniedTooltip} align="top" sideAlign="left" disabled={hasNotesPermission}>
                          <Button disabled={criteria?.questions.length < 1 || !hasNotesPermission} colour={"#24394F"} clickHandler={nextPageClickHandler}>
                            Back to diagnostic
                            <img src="/img/sparkle.svg" alt="AI Sparkle" />
                          </Button>
                        </Tooltip>
                      </Tooltip>

                      <Tooltip text={generateAssessmentDeniedTooltip} align={'top'} sideAlign="left" disabled={hasNotesPermission}>
                        <Tooltip text={`Your role as a ${rolePermissions?.roleTitle} means you cannot generate an assessment`} align={'top'} sideAlign="left" disabled={rolePermissions?.generateAssessment}>
                          <Tooltip text="There are no diagnostic questions found in this criteria." align="top" sideAlign="left" disabled={criteria?.questions.length > 0}>
                            <Button disabled={!rolePermissions?.generateAssessment || criteria?.questions.length < 1 || !hasNotesPermission} colour={"#24394F"} clickHandler={handleGenerateAgainClick}>Generate Again
                              <img src='/img/sparkle.svg' />
                            </Button>
                          </Tooltip>
                        </Tooltip>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        }
      </div>

      </>
    )
  }
  else{

    return <></>
  }
  
}

export default QuestionSectionCriteria